/**
 * useAutoTranslate — hook + component that auto-translates dynamic
 * user-generated / admin content into the reader's currently-selected UI
 * language.
 *
 *   - Reads i18n.language via useTranslation().
 *   - When language !== 'en', batches the visible text into a single call to
 *     `POST /api/translate` (backend → Claude Haiku 4.5, MongoDB cache).
 *   - Coalesces requests across the whole page for ~120ms so the community
 *     feed only fires one network round trip.
 *   - Caches results in a per-tab Map keyed by `${hash}::${lang}`.
 */
import { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import { LANG_CODE_TO_NAME } from '../i18n';

/** ---------- per-tab cache + inflight coalescer ---------------------------
 *  cache          : Map<`${hash}::${lang}`, translated:string>
 *  pending        : Map<lang, { items: [{key,text}], resolvers: Fn[], timer }>
 */
const cache = new Map();
const pending = new Map();

const djb2 = (s) => {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) + s.charCodeAt(i);
  return String(h >>> 0);
};

const cacheKey = (text, lang) => `${djb2(text)}::${lang}`;

/** Schedule a translation. Returns a promise that resolves with the
 *  translated string. Multiple concurrent calls for the same language are
 *  merged into a single HTTP request. */
const scheduleTranslate = (apiBase, text, lang) => new Promise((resolve) => {
  const ck = cacheKey(text, lang);
  if (cache.has(ck)) {
    resolve(cache.get(ck));
    return;
  }

  let bucket = pending.get(lang);
  if (!bucket) {
    bucket = { items: [], resolvers: [], timer: null };
    pending.set(lang, bucket);
  }
  bucket.items.push({ key: ck, text });
  bucket.resolvers.push({ key: ck, resolve });

  clearTimeout(bucket.timer);
  bucket.timer = setTimeout(async () => {
    pending.delete(lang);
    // De-dupe by key.
    const seen = new Set();
    const items = bucket.items
      .filter((it) => {
        if (seen.has(it.key)) return false;
        seen.add(it.key);
        return true;
      })
      .map((it) => ({ id: it.key, text: it.text }));

    try {
      const { data } = await axios.post(`${apiBase}/translate`, {
        items,
        target_language: lang,
      }, { timeout: 25000 });
      const out = Array.isArray(data?.items) ? data.items : [];
      out.forEach((row) => {
        if (row?.id && typeof row.translated === 'string' && row.translated.length) {
          cache.set(row.id, row.translated);
        }
      });
    } catch (err) {
      // Fall through — resolvers below will fall back to original text.
    }

    // Resolve every waiter with either the translated string or the source.
    bucket.resolvers.forEach(({ key, resolve: r }) => {
      const original = bucket.items.find((it) => it.key === key)?.text || '';
      r(cache.get(key) || original);
    });
  }, 120);
});

/** React hook: returns `{ translated, isTranslating, isTranslated }` for the
 *  supplied source text. Returns the source verbatim when language is `en`
 *  or when the text is empty. */
export function useAutoTranslate(source) {
  const { API } = useContext(AuthContext) || { API: '' };
  const { i18n } = useTranslation();
  const langCode = (i18n?.language || 'en').split('-')[0];
  const langName = LANG_CODE_TO_NAME[langCode] || 'English';

  const [translated, setTranslated] = useState(source || '');
  const [isTranslating, setIsTranslating] = useState(false);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => {
    if (!source || !source.trim()) {
      setTranslated(source || '');
      return;
    }
    if (langCode === 'en' || langName === 'English') {
      setTranslated(source);
      return;
    }
    const ck = cacheKey(source, langName);
    if (cache.has(ck)) {
      setTranslated(cache.get(ck));
      return;
    }
    setIsTranslating(true);
    scheduleTranslate(API, source, langName)
      .then((t) => {
        if (mounted.current) {
          setTranslated(t || source);
        }
      })
      .finally(() => {
        if (mounted.current) setIsTranslating(false);
      });
  }, [source, langCode, langName, API]);

  const isTranslated =
    langCode !== 'en'
    && !!source
    && !!translated
    && translated !== source
    && !isTranslating;

  return { translated, isTranslating, isTranslated, targetLanguage: langName };
}

export default useAutoTranslate;
